import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "filterButton", "yearSelector", "yearButton", "exportButton", "modalContainer"]
  static values  = {
    listUrl: String,
    exportUrl: String,
    exportPdfUrl: String,
    filter: { type: String, default: "all" },
    year: String,
    mode: { type: String, default: "index" }
  }

  connect() {
    this.currentSort = "name"
    this.currentOrder = "asc"
  }

  // ── Filter ──────────────────────────────────────────────────────

  changeFilter(event) {
    const filter = event.currentTarget.dataset.filter
    this.filterValue = filter

    // Update button label
    if (this.hasFilterButtonTarget) {
      const label = event.currentTarget.textContent.trim()
      const arrow = '<svg class="w-2.5 h-2.5 ml-0.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg>'
      this.filterButtonTarget.innerHTML = `${this._escapeHtml(label)} ${arrow}`
    }

    // Show/hide year selector
    if (this.hasYearSelectorTarget) {
      if (filter === "by_year") {
        this.yearSelectorTarget.classList.remove("hidden")
      } else {
        this.yearSelectorTarget.classList.add("hidden")
      }
    }

    // Close dropdown
    const menu = event.currentTarget.closest('[data-dropdown-target="menu"]')
    if (menu) menu.classList.add("hidden")

    this._reloadFrame()
  }

  changeYear(event) {
    const year = event.currentTarget.dataset.year
    this.yearValue = year

    // Update button label
    if (this.hasYearButtonTarget) {
      const arrow = '<svg class="w-2.5 h-2.5 ml-0.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg>'
      this.yearButtonTarget.innerHTML = `${this._escapeHtml(year)} ${arrow}`
    }

    // Close dropdown
    const menu = event.currentTarget.closest('[data-dropdown-target="menu"]')
    if (menu) menu.classList.add("hidden")

    this._reloadFrame()
  }

  // ── Sort (index only) ──────────────────────────────────────────

  sortByColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this.currentSort === key) {
      this.currentOrder = this.currentOrder === "asc" ? "desc" : "asc"
    } else {
      this.currentSort = key
      this.currentOrder = "asc"
    }
    this._reloadFrame()
  }

  // ── Expand/collapse detail (show only) ─────────────────────────

  toggleDetail(event) {
    // Don't toggle if clicking a link
    if (event.target.closest("a")) return

    const detailId = event.currentTarget.dataset.detailId
    if (!detailId) return

    const detailRow = document.getElementById(detailId)
    if (!detailRow) return

    const chevron = event.currentTarget.querySelector(`[data-chevron-for="${detailId}"]`)

    if (detailRow.classList.contains("hidden")) {
      detailRow.classList.remove("hidden")
      if (chevron) chevron.classList.add("rotate-180")
    } else {
      detailRow.classList.add("hidden")
      if (chevron) chevron.classList.remove("rotate-180")
    }
  }

  // ── Export ──────────────────────────────────────────────────────

  exportCsv(event) {
    this._closeExportDropdown(event)

    if (!this.hasExportUrlValue || !this.exportUrlValue) return

    this._showExportSpinner()

    const params = new URLSearchParams()
    params.set("filter", this.filterValue)
    if (this.filterValue === "by_year" && this.yearValue) {
      params.set("year", this.yearValue)
    }
    const url = `${this.exportUrlValue}?${params.toString()}`

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `vendor-evaluations-${today}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
        this._showToast("Export downloaded successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  exportPdf(event) {
    this._closeExportDropdown(event)

    if (!this.hasExportPdfUrlValue || !this.exportPdfUrlValue) return

    this._showExportSpinner()

    const params = new URLSearchParams()
    params.set("filter", this.filterValue)
    if (this.filterValue === "by_year" && this.yearValue) {
      params.set("year", this.yearValue)
    }
    const url = `${this.exportPdfUrlValue}?${params.toString()}`

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `vendor-evaluations-${today}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
        this._showToast("Export downloaded successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  // ── Vendor Modal ───────────────────────────────────────────────

  showVendorModal(event) {
    const builderId = event.currentTarget.dataset.builderId
    if (!builderId) return

    const params = new URLSearchParams()
    params.set("filter", this.filterValue)
    if (this.filterValue === "by_year" && this.yearValue) {
      params.set("year", this.yearValue)
    }

    const url = `/company/evaluations/${builderId}/vendor_modal?${params.toString()}`

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to load")
        return response.text()
      })
      .then((html) => {
        if (!this.hasModalContainerTarget) return
        this.modalContainerTarget.innerHTML = html
        document.body.style.overflow = "hidden"
        this._modalKeydown = (e) => {
          if (e.key === "Escape") this.closeVendorModal()
        }
        document.addEventListener("keydown", this._modalKeydown)
      })
      .catch(() => this._showToast("Failed to load evaluations.", "error"))
  }

  closeVendorModal() {
    if (this.hasModalContainerTarget) {
      this.modalContainerTarget.innerHTML = ""
    }
    document.body.style.overflow = ""
    if (this._modalKeydown) {
      document.removeEventListener("keydown", this._modalKeydown)
      this._modalKeydown = null
    }
  }

  backdropMousedown(event) {
    this._mousedownTarget = event.target
  }

  closeOnBackdrop(event) {
    // Only close if click started and ended on the overlay (not dragged from dialog)
    if (event.target === event.currentTarget && this._mousedownTarget === event.currentTarget) {
      this.closeVendorModal()
    }
    this._mousedownTarget = null
  }

  toggleModalDetail(event) {
    if (event.target.closest("a")) return
    const detailId = event.currentTarget.dataset.detailId
    if (!detailId) return

    const detailRows = document.querySelectorAll(`[data-modal-detail="${detailId}"]`)
    detailRows.forEach((row) => row.classList.toggle("hidden"))
  }

  modalExportExcel(event) {
    this._modalExport(event, "csv")
  }

  modalExportPdf(event) {
    this._modalExport(event, "pdf")
  }

  _modalExport(event, format) {
    const builderId = event.currentTarget.dataset.builderId
    if (!builderId) return

    // Close the dropdown
    const menu = event.currentTarget.closest('[data-dropdown-target="menu"]')
    if (menu) menu.classList.add("hidden")

    const params = new URLSearchParams()
    params.set("filter", this.filterValue)
    if (this.filterValue === "by_year" && this.yearValue) {
      params.set("year", this.yearValue)
    }

    const action = format === "pdf" ? "export_show_pdf" : "export_show_csv"
    const url = `/company/evaluations/${builderId}/${action}?${params.toString()}`
    const ext = format === "pdf" ? "pdf" : "csv"

    const btn = document.getElementById("vendor-modal-export-btn")
    let originalHTML
    if (btn) {
      originalHTML = btn.innerHTML
      btn.disabled = true
      btn.innerHTML = '<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = blobUrl
        const today = new Date().toISOString().split("T")[0]
        a.download = `vendor-evaluations-${today}.${ext}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(blobUrl)
        this._showToast("Export downloaded successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => {
        if (btn && originalHTML) {
          btn.innerHTML = originalHTML
          btn.disabled = false
        }
      })
  }

  // ── Internals ──────────────────────────────────────────────────

  _closeExportDropdown(event) {
    const menu = event?.currentTarget?.closest('[data-dropdown-target="menu"]')
    if (menu) menu.classList.add("hidden")
  }

  _reloadFrame() {
    const frame = this.hasFrameTarget ? this.frameTarget : null
    if (!frame) return

    const params = new URLSearchParams()
    params.set("format", "html")
    params.set("page", "1")
    params.set("filter", this.filterValue)
    if (this.filterValue === "by_year" && this.yearValue) {
      params.set("year", this.yearValue)
    }

    // Sort params (index only)
    if (this.modeValue === "index") {
      params.set("sort_by", this.currentSort)
      params.set("sort_order", this.currentOrder)
    }

    frame.src = `${this.listUrlValue}?${params.toString()}`
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = '<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
